// 非課税制度の拠出枠。
//
// 預り区分（「NISAつみたて投資枠」「iDeCo」など）から制度を見分けて、
// 年間投資枠と非課税保有限度額（生涯枠）を返す。ライフプランの入力規制と、
// 将来の拠出を枠内に収める計算の両方がここを見る。
//
// 2024 年以降の NISA（新 NISA）:
//   つみたて投資枠  年 120 万円
//   成長投資枠      年 240 万円（生涯 1,200 万円まで）
//   合わせて        年 360 万円 / 非課税保有限度額 1,800 万円（簿価）
// こども NISA（2027 年 1 月開始予定。ジュニア NISA の後継）:
//   0〜17 歳が対象  年 60 万円 / 非課税保有限度額 600 万円
//   つみたて投資枠と同じ投資信託が対象で、非課税保有期間は無期限。18 歳になると
//   成人の NISA に移り、本人の 1,800 万円の枠の中で管理される
// 旧制度（2023 年で新規拠出終了）:
//   つみたて NISA   年 40 万円 / 一般 NISA 年 120 万円 / ジュニア NISA 年 80 万円
// その他:
//   iDeCo          年 81.6 万円（第 1 号被保険者の上限。加入区分で変わる）
//   小規模企業共済  年 84 万円（月 7 万円）
const NISA_LIFETIME = 18_000_000;
const NISA_GROWTH_LIFETIME = 12_000_000;
const KODOMO_NISA_LIFETIME = 6_000_000;
const LIMITS = {
    nisa: {
        annualLimit: 3_600_000,
        lifetimeLimit: NISA_LIFETIME,
        lifetimeGroup: "nisa",
        groupLifetimeLimit: NISA_LIFETIME,
        closed: false,
        annualLimitVaries: false,
        eligibleAgeBelow: null,
    },
    nisa_tsumitate: {
        annualLimit: 1_200_000,
        lifetimeLimit: NISA_LIFETIME,
        lifetimeGroup: "nisa",
        groupLifetimeLimit: NISA_LIFETIME,
        closed: false,
        annualLimitVaries: false,
        eligibleAgeBelow: null,
    },
    nisa_growth: {
        annualLimit: 2_400_000,
        // 成長投資枠は 1,800 万円の内枠として 1,200 万円まで
        lifetimeLimit: NISA_GROWTH_LIFETIME,
        lifetimeGroup: "nisa",
        groupLifetimeLimit: NISA_LIFETIME,
        closed: false,
        annualLimitVaries: false,
        eligibleAgeBelow: null,
    },
    kodomo_nisa: {
        annualLimit: 600_000,
        // 600 万円はこども NISA の内枠。18 歳で成人の NISA に移り、
        // 本人の 1,800 万円の中で管理される
        lifetimeLimit: KODOMO_NISA_LIFETIME,
        lifetimeGroup: "nisa",
        groupLifetimeLimit: NISA_LIFETIME,
        closed: false,
        annualLimitVaries: false,
        eligibleAgeBelow: 18,
    },
    nisa_old_tsumitate: {
        annualLimit: 400_000,
        lifetimeLimit: null,
        lifetimeGroup: null,
        groupLifetimeLimit: null,
        closed: true,
        annualLimitVaries: false,
        eligibleAgeBelow: null,
    },
    nisa_old_general: {
        annualLimit: 1_200_000,
        lifetimeLimit: null,
        lifetimeGroup: null,
        groupLifetimeLimit: null,
        closed: true,
        annualLimitVaries: false,
        eligibleAgeBelow: null,
    },
    junior_nisa: {
        annualLimit: 800_000,
        lifetimeLimit: null,
        lifetimeGroup: null,
        groupLifetimeLimit: null,
        closed: true,
        annualLimitVaries: false,
        eligibleAgeBelow: null,
    },
    ideco: {
        // 第 1 号被保険者（自営業）の上限。会社員・公務員はこれより低い
        annualLimit: 816_000,
        lifetimeLimit: null,
        lifetimeGroup: null,
        groupLifetimeLimit: null,
        closed: false,
        annualLimitVaries: true,
        eligibleAgeBelow: null,
    },
    kyosai: {
        annualLimit: 840_000,
        lifetimeLimit: null,
        lifetimeGroup: null,
        groupLifetimeLimit: null,
        closed: false,
        annualLimitVaries: false,
        eligibleAgeBelow: null,
    },
    unknown: {
        annualLimit: null,
        lifetimeLimit: null,
        lifetimeGroup: null,
        groupLifetimeLimit: null,
        closed: false,
        annualLimitVaries: false,
        eligibleAgeBelow: null,
    },
};
/**
 * 預り区分の名前から制度を見分ける。
 *
 * 区分名は証券会社ごとに呼び方が違い、Zeni でも自由入力なので、
 * 手がかりになる語で寄せる。判定できないものは unknown（枠の規制なし）。
 */
export function detectContributionScheme(accountType) {
    const text = accountType.toLowerCase().replace(/[\s\u3000]/g, "");
    if (text === "")
        return "unknown";
    // こども NISA・ジュニア NISA は「NISA」も「つみたて」も含みうるので最初に見る
    if (text.includes("こども") ||
        text.includes("子ども") ||
        text.includes("子供") ||
        text.includes("kodomo")) {
        return "kodomo_nisa";
    }
    if (text.includes("ジュニア") || text.includes("junior"))
        return "junior_nisa";
    // 新制度の枠名。「NISAつみたて投資枠」「成長投資枠」など
    if (text.includes("つみたて投資枠") || text.includes("積立投資枠"))
        return "nisa_tsumitate";
    if (text.includes("成長投資枠"))
        return "nisa_growth";
    if (text.includes("ideco") || text.includes("確定拠出"))
        return "ideco";
    if (text.includes("共済"))
        return "kyosai";
    // 旧制度
    if (text.includes("つみたてnisa") || text.includes("積立nisa"))
        return "nisa_old_tsumitate";
    if (text.includes("一般nisa"))
        return "nisa_old_general";
    // 枠の区別が付いていない「NISA」は新制度の枠全体として扱う
    if (text.includes("nisa"))
        return "nisa";
    return "unknown";
}
export function contributionLimitOf(scheme) {
    return { scheme, ...LIMITS[scheme] };
}
/** 預り区分から直接、拠出枠を引く */
export function contributionLimitForAccountType(accountType) {
    return contributionLimitOf(detectContributionScheme(accountType));
}
//# sourceMappingURL=contribution-limit.js.map